import {
  CloseOutlined,
  CloudUploadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Collapse,
  Row,
  Select,
  Checkbox,
  Upload,
  Input,
} from 'antd';
import PropTypes from 'prop-types';
import { Controller, useFieldArray } from 'react-hook-form';
import withStyles from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useCallback, useEffect } from 'react';

import GetField from '../Components/GetField';
import StyleForm from './StyleForm';
import styles from '../styles';
import Api from '../Api';
import { errorNotification } from '../Snackbar';
import { getNewFileName } from '../utils';
import { deleteJobchartProduct, setJobchartFiles } from '../Actions';
import ShowFiles from './ShowFiles';

const { Panel } = Collapse;

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const AntCheckBox = GetField(Checkbox);

const { Option } = Select;

const ProductForm = props => {
  const {
    productItem,
    productIndex,
    productRemove,
    classes,
    getValues,
    control,
    setValue,
    watch,
    unregister,
  } = props;

  const productIds = useSelector(state => state.productIds);

  const dispatch = useDispatch();

  const [FabricsForJobchart, setFabricsForJobchart] = useState({
    fabricIndexes: {},
    fabricNames: [],
    widths: {},
  });

  const [AccessoriesForJobchart, setAccessoriesForJobchart] = useState([]);

  const {
    fields: styleFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `products[${productIndex}].styles`,
  });

  const GetFabrics = useCallback(async (productId, isAll) => {
    try {
      const { data } = await Api.get(
        '/fabric-inventory/fabricsforjobchart',
        !isAll ? { productId: productId || null } : {}
      );

      setFabricsForJobchart(data);
    } catch (error) {
      errorNotification(error.message);
    }
  }, []);

  const GetAccessories = useCallback(async (productId, isAll) => {
    try {
      const { data } = await Api.get(
        '/accessory-inventory/accessoriesforjobchart',
        !isAll ? { productId } : {}
      );

      setAccessoriesForJobchart(data);
    } catch (error) {
      errorNotification(error.message);
    }
  }, []);

  const styleAppend = useCallback(() => {
    append({ fabrics: [{}], accessories: [{}] });
  }, [append]);

  useEffect(() => {
    const isAll = getValues(`products[${productIndex}].isAll`);
    const productId = getValues(`products[${productIndex}].productId`);

    if (productId) {
      GetFabrics(productId, isAll);
      GetAccessories(productId, isAll);
    }
  }, [
    watch(`products[${productIndex}].productId`),
    watch(`products[${productIndex}].isAll`),
  ]);

  const beforeUpload = useCallback(_file => {
    dispatch(
      setJobchartFiles(
        productIndex,
        new File([_file], getNewFileName(_file.name), { type: _file.type })
      )
    );
    return false;
  }, []);

  return (
    <>
      <Collapse key={productIndex}>
        <Panel
          header={`Product ${productIndex + 1}`}
          key="1"
          extra={
            <CloseOutlined
              onClick={() => {
                const products = getValues('products');
                if (products && products.length > 1) {
                  productRemove(productIndex);
                  dispatch(deleteJobchartProduct(productIndex));
                }
              }}
            />
          }
        >
          <Row>
            <Col xs={6}>
              <Controller
                name={`products[${productIndex}].productId`}
                control={control}
                defaultValue={productItem.productId}
                render={({ field }) => (
                  <AntSelect
                    allowClear
                    label="Product"
                    placeholder="Select Product"
                    {...field}
                  >
                    {productIds.map(product => (
                      <Option key={product.id} value={product.id}>
                        {product.name}
                      </Option>
                    ))}
                  </AntSelect>
                )}
              />
            </Col>
            &nbsp; &nbsp;
            <Col xs={6}>
              <Controller
                name={`products[${productIndex}].remark`}
                control={control}
                defaultValue={productItem.remark}
                render={({ field }) => (
                  <AntInput
                    allowClear
                    label="Remark"
                    placeholder="Remark"
                    {...field}
                  />
                )}
              />
            </Col>
            &nbsp; &nbsp;
            <Col xs={4}>
              <Controller
                name={`products[${productIndex}].isAll`}
                control={control}
                defaultValue={productItem.isAll || false}
                render={({ field }) => (
                  <AntCheckBox
                    allowClear
                    {...field}
                    label="  "
                    checked={field.value === true}
                  />
                )}
              />
            </Col>
            &nbsp; &nbsp;
            <Col xs={4} className={classes.buttonContainer}>
              <Upload multiple beforeUpload={beforeUpload} fileList={[]}>
                <Button icon={<CloudUploadOutlined />}>Select Files</Button>
              </Upload>
            </Col>
          </Row>

          <ShowFiles
            productIndex={productIndex}
            getValues={getValues}
            watch={watch}
            setValue={setValue}
            unregister={unregister}
          />

          {styleFields.map((item, index) => (
            <StyleForm
              key={item.id}
              productIndex={productIndex}
              styleItem={item}
              styleIndex={index}
              stylesRemove={remove}
              classes={classes}
              getValues={getValues}
              setValue={setValue}
              control={control}
              FabricsForJobchart={FabricsForJobchart}
              AccessoriesForJobchart={AccessoriesForJobchart}
              watch={watch}
            />
          ))}
          <br />
          <Row className={classes.buttonContainer}>
            <Button type="primary" htmlType="button" onClick={styleAppend}>
              <PlusOutlined /> Style
            </Button>
          </Row>
        </Panel>
      </Collapse>
    </>
  );
};

ProductForm.propTypes = {
  classes: PropTypes.object.isRequired,
  productItem: PropTypes.object.isRequired,
  productIndex: PropTypes.number.isRequired,
  productRemove: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  unregister: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
};

export default withStyles(styles)(ProductForm);
